
import { useState, useEffect, useCallback } from 'react';
import { TimeItem, TimesContent } from '../types';
import { useTimesDatabase } from './useTimesDatabase';
import { toast } from "@/components/ui/use-toast";

export interface UseTimesEditorProps {
  content?: any;
  onContentChange?: (field: string, value: any) => void;
  currentUserId?: string;
}

export const useTimesEditor = ({
  content,
  onContentChange,
  currentUserId
}: UseTimesEditorProps) => {
  // Extract content from the correct location
  const initialContent = content?.content || content || {};
  const [blockTitle, setBlockTitle] = useState(initialContent?.blockTitle || "זמנים");
  const [times, setTimes] = useState<TimeItem[]>(initialContent?.times || []);
  const [activeTab, setActiveTab] = useState("edit");
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const {
    isLoading,
    fetchTimesFromDatabase,
    addTimeToDatabase,
    updateTimeInDatabase,
    deleteTimeFromDatabase,
    createNewTime
  } = useTimesDatabase(currentUserId);
  
  // Initialize times from database if content is empty
  useEffect(() => {
    console.log("Checking if we need to fetch times:", {
      hasInitialContent: !!initialContent?.times,
      initialContentLength: initialContent?.times?.length,
      hasTimes: !!times,
      timesLength: times?.length
    });
    
    if ((!initialContent?.times || initialContent.times.length === 0) && 
        (!times || times.length === 0)) {
      initializeFromDatabase();
    }
  }, []);

  const initializeFromDatabase = async () => {
    const fetchedTimes = await fetchTimesFromDatabase();
    if (fetchedTimes) {
      setTimes(fetchedTimes);
      // We do a manual update only on initial fetch
      if (onContentChange) {
        onContentChange('', {
          blockTitle,
          times: fetchedTimes
        });
        setHasUnsavedChanges(false);
      }
    }
  };
  
  const handleTimeChange = (updatedTimes: TimeItem[]) => {
    setTimes(updatedTimes);
    setHasUnsavedChanges(true);
  };

  // Debounced block title update - only marks as changed, doesn't save
  const handleUpdateBlockTitle = useCallback((value: string) => {
    setBlockTitle(value);
    setHasUnsavedChanges(true);
  }, []);

  const handleAddTime = async () => {
    const newTime = createNewTime();
    const addedTime = await addTimeToDatabase(newTime);
    
    if (addedTime) {
      // Update local state without triggering parent update
      const updatedTimes = [...times, addedTime];
      setTimes(updatedTimes);
      setHasUnsavedChanges(true);
    }
  };

  // Update time locally without database save - only mark as changed
  const handleUpdateTime = useCallback((id: string, field: string, value: string | boolean | number) => {
    const updatedTimes = times.map(time => {
      if (time.id === id) {
        return { ...time, [field]: value };
      }
      return time;
    });
    
    setTimes(updatedTimes);
    setHasUnsavedChanges(true);
  }, [times]);

  const handleDeleteTime = async (id: string) => {
    const success = await deleteTimeFromDatabase(id);
    
    if (success) {
      // Update local state without triggering parent update
      const updatedTimes = times.filter(time => time.id !== id);
      setTimes(updatedTimes);
      setHasUnsavedChanges(true);
    }
  };

  const handleSaveChanges = async () => {
    if (!currentUserId) {
      toast({
        variant: "destructive",
        title: "שגיאה בשמירת השינויים",
        description: "יש להתחבר כדי לשמור שינויים"
      });
      return;
    }

    setIsSaving(true);
    
    try {
      // Save all time updates to database
      for (const time of times) {
        // Update each time in the database
        await updateTimeInDatabase(time.id, 'title', time.title);
        await updateTimeInDatabase(time.id, 'time', time.time);
        await updateTimeInDatabase(time.id, 'show_time', time.show_time);
        await updateTimeInDatabase(time.id, 'is_relative', time.is_relative);
        await updateTimeInDatabase(time.id, 'relative_to', time.relative_to);
        await updateTimeInDatabase(time.id, 'offset_minutes', time.offset_minutes);
      }
      
      if (onContentChange) {
        const newContent = {
          blockTitle,
          times
        };
        onContentChange('', newContent); // Use empty string for field to replace entire content
      }
      
      setHasUnsavedChanges(false);
      
      toast({
        title: "השינויים נשמרו",
        description: "הזמנים עודכנו בהצלחה"
      });
    } catch (error) {
      console.error("Error saving changes:", error);
      toast({
        variant: "destructive",
        title: "שגיאה בשמירת השינויים",
        description: "אירעה שגיאה בעת שמירת השינויים"
      });
    } finally {
      setIsSaving(false);
    }
  };

  return {
    blockTitle,
    times,
    activeTab,
    isSaving,
    isLoading,
    hasUnsavedChanges,
    setActiveTab,
    handleTimeChange,
    handleUpdateBlockTitle,
    handleAddTime,
    handleUpdateTime,
    handleDeleteTime,
    handleSaveChanges
  };
};
